import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ActionIcon,
    Alert,
    Anchor,
    Badge,
    Button,
    Card,
    Divider,
    Grid,
    Group,
    LoadingOverlay,
    Paper,
    SegmentedControl,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title
} from "@mantine/core";
import { MdArrowBack, MdOpenInNew, MdRefresh, MdSave } from "react-icons/md";
import { getToken, useLogin } from "../../../authConfig";
import { getIdeaByIdApi, IdeaItem, IdeaReviewStatus, updateIdeaJiraLinkApi, updateIdeaReviewCommentApi, updateIdeaReviewStatusApi } from "../../../api";
import { TimezoneDateDisplay } from "../../../components/common/TimezoneDateDisplay";
import { CATEGORY_COLORS, CATEGORY_LABELS, IDEA_REVIEW_STATUS_OPTIONS, ideaReviewStatus } from "../../../components/idea-table/ideaLabels";

const IdeaDetails = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { ideaId } = useParams<{ ideaId: string }>();
    const client = useLogin ? useMsal().instance : undefined;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [idea, setIdea] = useState<IdeaItem | null>(null);
    const [reviewStatusSaving, setReviewStatusSaving] = useState(false);
    const [reviewStatusError, setReviewStatusError] = useState<string | null>(null);
    const [jiraLinkInput, setJiraLinkInput] = useState<string>("");
    const [jiraSaving, setJiraSaving] = useState(false);
    const [jiraError, setJiraError] = useState<string | null>(null);
    const [commentInput, setCommentInput] = useState<string>("");
    const [commentSaving, setCommentSaving] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);

    const backToInbox = () => navigate(`/administration/ideas/inbox?${searchParams.toString()}`);

    const loadIdea = async () => {
        if (!ideaId) return;

        setLoading(true);
        setError(null);
        setReviewStatusError(null);
        setJiraError(null);
        setCommentError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching the idea");
            }
            const ideaData = await getIdeaByIdApi(ideaId, idToken);
            setIdea(ideaData);
            setJiraLinkInput(ideaData.jira_link ?? "");
            setCommentInput(ideaData.review_comment ?? "");
        } catch (err) {
            console.error("Failed to fetch idea", err);
            setError(err instanceof Error ? err.message : "Failed to load idea details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadIdea();
    }, [ideaId, client]);

    const saveReviewStatus = async (value: string) => {
        if (!ideaId) return;
        setReviewStatusSaving(true);
        setReviewStatusError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateIdeaReviewStatusApi(ideaId, value as IdeaReviewStatus, idToken);
            setIdea(updated);
        } catch (err) {
            console.error("Failed to save review status", err);
            setReviewStatusError(err instanceof Error ? err.message : "Failed to save review status");
        } finally {
            setReviewStatusSaving(false);
        }
    };

    const saveJiraLink = async () => {
        if (!ideaId) return;
        const trimmed = jiraLinkInput.trim();
        if (trimmed && !trimmed.startsWith("https://")) {
            setJiraError("URL must start with https://");
            return;
        }
        setJiraSaving(true);
        setJiraError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateIdeaJiraLinkApi(ideaId, trimmed || null, idToken);
            setIdea(updated);
            setJiraLinkInput(updated.jira_link ?? "");
        } catch (err) {
            setJiraError(err instanceof Error ? err.message : "Failed to save Jira link");
        } finally {
            setJiraSaving(false);
        }
    };

    const saveComment = async () => {
        if (!ideaId) return;
        setCommentSaving(true);
        setCommentError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateIdeaReviewCommentApi(ideaId, commentInput.trim() || null, idToken);
            setIdea(updated);
            setCommentInput(updated.review_comment ?? "");
        } catch (err) {
            setCommentError(err instanceof Error ? err.message : "Failed to save comment");
        } finally {
            setCommentSaving(false);
        }
    };

    if (!ideaId) {
        return (
            <Paper p="md">
                <Alert color="red" title="Error">
                    Idea ID is required to view idea details.
                </Alert>
            </Paper>
        );
    }

    return (
        <Stack gap="md" pos="relative">
            <LoadingOverlay visible={loading} />

            <Group justify="space-between" align="center">
                <Group>
                    <ActionIcon variant="light" onClick={backToInbox} size="lg">
                        <MdArrowBack size={18} />
                    </ActionIcon>
                    <div>
                        <Title order={3}>Idea Details</Title>
                        <Text size="sm" c="dimmed">
                            {idea?.username || "N/A"} •{" "}
                            {idea ? <TimezoneDateDisplay timestamp={idea.timestamp} showTime={true} showTimezone={false} size="sm" /> : ""}
                        </Text>
                    </div>
                </Group>
                <Button variant="light" leftSection={<MdRefresh size={16} />} onClick={loadIdea} disabled={loading}>
                    Refresh
                </Button>
            </Group>

            {error && <Alert color="red">{error}</Alert>}

            {idea && (
                <Paper p="md">
                    <Grid gap="lg">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Card withBorder radius="lg" shadow="sm">
                                <Stack gap="sm">
                                    <Title order={5}>Idea</Title>
                                    <Divider my="sm" />

                                    <Group gap="xs">
                                        <Badge color={CATEGORY_COLORS[idea.category] || "gray"} variant="light">
                                            {CATEGORY_LABELS[idea.category] || idea.category}
                                        </Badge>
                                        {idea.status === "draft" && (
                                            <Badge color="yellow" variant="outline">
                                                Draft
                                            </Badge>
                                        )}
                                    </Group>

                                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                                        {idea.idea}
                                    </Text>

                                    <Group grow align="flex-start">
                                        <Stack gap="xs">
                                            <Text fw={500}>Name</Text>
                                            <Text size="sm" c="dimmed">
                                                {idea.name || "N/A"}
                                            </Text>
                                        </Stack>
                                        <Stack gap="xs">
                                            <Text fw={500}>Username</Text>
                                            <Text size="sm" c="dimmed">
                                                {idea.username || "N/A"}
                                            </Text>
                                        </Stack>
                                    </Group>

                                    <Stack gap="xs">
                                        <Text fw={500}>Submitted</Text>
                                        <TimezoneDateDisplay timestamp={idea.timestamp} showTime={true} showTimezone={true} size="sm" color="dimmed" />
                                    </Stack>

                                    {idea.conversation_id && (
                                        <Stack gap="xs">
                                            <Text fw={500}>Conversation</Text>
                                            <Anchor component={Link} to={`/administration/history/${idea.conversation_id}`} size="sm">
                                                {idea.conversation_id}
                                            </Anchor>
                                        </Stack>
                                    )}
                                </Stack>
                            </Card>
                        </Grid.Col>

                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Card withBorder radius="lg" shadow="sm">
                                <Stack gap="sm">
                                    <Title order={5}>Idea Review</Title>
                                    <Divider my="sm" />

                                    {idea.status !== "confirmed" ? (
                                        <Alert color="yellow">Draft ideas can be reviewed once the user has confirmed them.</Alert>
                                    ) : (
                                        <>
                                            <Stack gap="xs">
                                                <Text fw={500}>Review Status</Text>
                                                <SegmentedControl
                                                    value={ideaReviewStatus(idea.review_status)}
                                                    onChange={saveReviewStatus}
                                                    disabled={reviewStatusSaving}
                                                    data={IDEA_REVIEW_STATUS_OPTIONS}
                                                    size="sm"
                                                />
                                                {reviewStatusError && (
                                                    <Text size="xs" c="red">
                                                        {reviewStatusError}
                                                    </Text>
                                                )}
                                            </Stack>

                                            <Stack gap="xs">
                                                <Text fw={500}>Jira Link</Text>
                                                {idea.jira_link && (
                                                    <Anchor
                                                        href={idea.jira_link}
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                        size="sm"
                                                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                                    >
                                                        {idea.jira_link}
                                                        <MdOpenInNew size={14} />
                                                    </Anchor>
                                                )}
                                                <Group gap="xs" align="flex-end">
                                                    <TextInput
                                                        style={{ flex: 1 }}
                                                        placeholder="https://jira.example.com/browse/IDEA-123"
                                                        value={jiraLinkInput}
                                                        onChange={e => setJiraLinkInput(e.currentTarget.value)}
                                                        size="sm"
                                                        error={jiraError}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        leftSection={<MdSave size={16} />}
                                                        onClick={saveJiraLink}
                                                        loading={jiraSaving}
                                                        disabled={jiraLinkInput === (idea.jira_link ?? "")}
                                                    >
                                                        Save
                                                    </Button>
                                                </Group>
                                            </Stack>

                                            <Stack gap="xs">
                                                <Group justify="space-between" align="baseline">
                                                    <Text fw={500}>Comment</Text>
                                                    <Text size="xs" c="dimmed">
                                                        {commentInput.length}/1000
                                                    </Text>
                                                </Group>
                                                <Textarea
                                                    placeholder="Explain the review decision..."
                                                    value={commentInput}
                                                    onChange={e => setCommentInput(e.currentTarget.value.slice(0, 1000))}
                                                    size="sm"
                                                    autosize
                                                    minRows={2}
                                                    maxRows={6}
                                                    error={commentError}
                                                />
                                                <Group justify="flex-end">
                                                    <Button
                                                        size="sm"
                                                        leftSection={<MdSave size={16} />}
                                                        onClick={saveComment}
                                                        loading={commentSaving}
                                                        disabled={commentInput.trim() === (idea.review_comment ?? "").trim()}
                                                    >
                                                        Save
                                                    </Button>
                                                </Group>
                                            </Stack>
                                        </>
                                    )}
                                </Stack>
                            </Card>
                        </Grid.Col>
                    </Grid>
                </Paper>
            )}
        </Stack>
    );
};

export default IdeaDetails;
